import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { usePreviewGoogleReviewAutoReply } from '../../../../queries';
import { Button } from '../../../components-v2/Button';
import { Pill } from '../../../components-v2/Pill/Pill';
import { THEME } from '../../../components-v2/Theme/theme';
import { StarRating } from '../../surveys/StarRating';
import { DEFAULT_MINIMUM_CONFIDENCE, DEFAULT_MINIMUM_STAR_RATING } from './constants';

interface IProps {
	children?: React.ReactNode;
	impersonationContext?: Api.IImpersonationContext;
	settings?: Api.IGoogleReviewAutoReplySettings;
	onSaveSettings?: () => void;
	isSavingSettings?: boolean;
	hasUnsavedSettings?: boolean;
}

export const ReviewAutoReplyPreview: React.FC<IProps> = ({
	children,
	impersonationContext,
	settings,
	onSaveSettings,
	isSavingSettings,
	hasUnsavedSettings,
}) => {
	const previewMutation = usePreviewGoogleReviewAutoReply({
		impersonationContext,
		onError: () => toast.error('Failed to preview AI replies'),
	});
	const preview = previewMutation.data;
	const isOff = (settings?.mode ?? Api.GoogleReviewAutoReplyMode.Off) === Api.GoogleReviewAutoReplyMode.Off;

	const onPreviewClicked = () => {
		previewMutation.mutate({
			minimumStarRating: settings?.minimumStarRating,
			minimumConfidence: settings?.minimumConfidence,
			maxReviews: 5,
		});
	};

	// Computed from the raw signals rather than the server verdicts so the chips update live as the
	// user changes the mode or thresholds after running a preview.
	const wouldAutoPost = (item: Api.IGoogleReviewAutoReplyPreviewItem) => {
		if (item.generationFailed) {
			return false;
		}
		switch (settings?.mode) {
			case Api.GoogleReviewAutoReplyMode.StarThreshold:
				return item.numberOfStars >= (settings.minimumStarRating ?? DEFAULT_MINIMUM_STAR_RATING);
			case Api.GoogleReviewAutoReplyMode.AIDiscretion:
				return (item.aiConfidence ?? 0) >= (settings.minimumConfidence ?? DEFAULT_MINIMUM_CONFIDENCE);
			default:
				return false;
		}
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.controls)}>
				{children}
				<div className={css(styleSheet.buttonRow)}>
					{onSaveSettings ? (
						<Button
							disabled={!hasUnsavedSettings}
							isLoading={isSavingSettings}
							onClick={onSaveSettings}
							size='sm'
							type='button'
							variant='primary'
						>
							Save Changes
						</Button>
					) : null}
					<Button
						isLoading={previewMutation.isPending}
						onClick={onPreviewClicked}
						size='sm'
						type='button'
						variant='secondary'
					>
						Preview on your recent reviews
					</Button>
				</div>
				{previewMutation.isPending ? (
					<p className={css(styleSheet.reasoning, styleSheet.pendingHint)}>
						Generating replies for your recent reviews. This can take up to a minute.
					</p>
				) : null}
			</div>
			<div className={css(styleSheet.results)}>
				{preview && !previewMutation.isPending ? (
					<>
						{isOff ? (
							<p className={css(styleSheet.reasoning)}>
								Auto-reply is off, so every reply below would wait for your approval. Pick a mode to see what AI would
								post automatically.
							</p>
						) : null}
						{preview.items.map(item => (
							<div className={css(styleSheet.item)} key={item.reviewId}>
								<div className={css(styleSheet.itemHeader)}>
									<span className={css(styleSheet.reviewerName)}>{item.reviewerName || 'Anonymous'}</span>
									<StarRating fillColor={THEME.colors.pill.yellow.icon} readonly total={5} value={item.numberOfStars} />
									{wouldAutoPost(item) ? (
										<Pill color='green' label='AI would reply' size='small' variant='label' />
									) : (
										<Pill color='orange' label='Would wait for you' size='small' variant='label' />
									)}
								</div>
								{item.reviewText ? <p className={css(styleSheet.reviewText)}>{item.reviewText}</p> : null}
								{item.generationFailed ? (
									<p className={css(styleSheet.reasoning)}>A reply could not be generated for this review.</p>
								) : (
									<>
										<p className={css(styleSheet.generatedReply)}>{item.generatedReply}</p>
										{settings?.mode === Api.GoogleReviewAutoReplyMode.AIDiscretion && item.aiReasoning ? (
											<p className={css(styleSheet.reasoning)}>
												{`AI confidence ${item.aiConfidence ?? 0}/100: ${item.aiReasoning}`}
											</p>
										) : null}
									</>
								)}
							</div>
						))}
						{!preview.items.length ? (
							<p className={css(styleSheet.reasoning)}>No recent Google reviews were found for this account.</p>
						) : null}
					</>
				) : null}
			</div>
		</div>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		display: 'grid',
		gap: THEME.spacing.lg,
		// Floors keep the panes from squeezing into each other; below the lg breakpoint the two-pane
		// layout gives way entirely rather than rendering a cramped middle zone.
		gridTemplateColumns: 'minmax(280px, 5fr) minmax(320px, 7fr)',
		paddingBottom: THEME.spacing.xxl,
		[THEME.queries.lg]: {
			gridTemplateColumns: '1fr',
		},
	},
	controls: {
		// Keeps the heading and mode controls in view while the results column scrolls.
		alignSelf: 'flex-start',
		position: 'sticky',
		top: THEME.spacing.md,
		[THEME.queries.lg]: {
			position: 'static',
		},
	},
	pendingHint: {
		marginTop: THEME.spacing.sm,
	},
	results: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	generatedReply: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
	},
	item: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.md,
	},
	itemHeader: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
	},
	buttonRow: {
		display: 'flex',
		gap: THEME.spacing.sm,
		marginTop: THEME.spacing.md,
	},
	reasoning: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		margin: 0,
	},
	reviewText: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		fontStyle: 'italic',
		margin: 0,
	},
	reviewerName: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
	},
});
